import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ShieldIcon, KeyIcon } from '../components/common/Icons';

type SetupStep = 'status' | 'generate' | 'verify' | 'recovery' | 'complete';

const MFASetupPage: React.FC = () => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<SetupStep>('status');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Status
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [recoveryCodesRemaining, setRecoveryCodesRemaining] = useState(0);

  // Setup
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  // Disable
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch('/api/auth/mfa/status');
      if (response.ok) {
        const data = await response.json();
        setMfaEnabled(data.mfa_enabled);
        setRecoveryCodesRemaining(data.recovery_codes_remaining);
        setStep('status');
      }
    } catch {
      setError('Failed to load MFA status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/auth/mfa/setup/totp', { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setQrCode(data.qr_code);
        setManualKey(data.manual_key);
        setStep('generate');
      } else {
        const err = await response.json().catch(() => ({ detail: 'Setup failed' }));
        setError(err.detail);
      }
    } catch {
      setError('Failed to start MFA setup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/auth/mfa/verify/totp', {
        method: 'POST',
        body: JSON.stringify({ code: verifyCode }),
      });
      if (response.ok) {
        const data = await response.json();
        setRecoveryCodes(data.recovery_codes);
        setStep('recovery');
      } else {
        const err = await response.json().catch(() => ({ detail: 'Invalid code' }));
        setError(err.detail);
        setVerifyCode('');
      }
    } catch {
      setError('Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/auth/mfa/totp', {
        method: 'DELETE',
        body: JSON.stringify({ code: disableCode }),
      });
      if (response.ok) {
        setMfaEnabled(false);
        setDisableCode('');
        setStep('status');
      } else {
        const err = await response.json().catch(() => ({ detail: 'Invalid code' }));
        setError(err.detail);
        setDisableCode('');
      }
    } catch {
      setError('Failed to disable MFA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateCodes = async () => {
    const code = prompt('Enter your current 6-digit TOTP code to regenerate recovery codes:');
    if (!code || code.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/auth/mfa/recovery-codes/regenerate', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      if (response.ok) {
        const data = await response.json();
        setRecoveryCodes(data.recovery_codes);
        setStep('recovery');
      } else {
        const err = await response.json().catch(() => ({ detail: 'Invalid code' }));
        setError(err.detail);
      }
    } catch {
      setError('Failed to regenerate codes');
    } finally {
      setIsLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
  };

  if (isLoading && step === 'status') {
    return (
      <div className="page-container">
        <div className="glass-panel" style={{ padding: '32px', textAlign: 'center' }}>
          Loading MFA status...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ShieldIcon size={24} />
            <div>
              <h1 className="page-title">Multi-Factor Authentication</h1>
              <p className="page-subtitle">
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#ef4444',
          fontSize: 'var(--text-sm)',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {/* Status View */}
      {step === 'status' && (
        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>TOTP Authenticator</h3>
            <span className={`badge ${mfaEnabled ? 'badge-success' : 'badge-neutral'}`}>
              {mfaEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          {mfaEnabled ? (
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginBottom: '16px' }}>
                Your account is protected with TOTP two-factor authentication.
                You have <strong>{recoveryCodesRemaining}</strong> recovery code{recoveryCodesRemaining !== 1 ? 's' : ''} remaining.
              </p>
              {recoveryCodesRemaining < 3 && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  color: '#f59e0b',
                  fontSize: 'var(--text-sm)',
                  marginBottom: '16px',
                }}>
                  Low recovery codes. Consider regenerating them.
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={handleRegenerateCodes} disabled={isLoading}>
                  <KeyIcon size={16} /> Regenerate Recovery Codes
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setStep('complete')}
                  disabled={isLoading}
                >
                  Disable MFA
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginBottom: '16px' }}>
                Protect your account by requiring a second verification step when signing in.
                You'll need an authenticator app like Google Authenticator, Authy, or 1Password.
              </p>
              <button className="btn btn-primary" onClick={handleSetup} disabled={isLoading}>
                <ShieldIcon size={16} /> Set Up MFA
              </button>
            </div>
          )}
        </div>
      )}

      {/* Generate Step — Show QR Code */}
      {step === 'generate' && (
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>Step 1: Scan QR Code</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            Open your authenticator app and scan this QR code.
          </p>

          <div style={{ textAlign: 'center', margin: '24px 0' }}>
            {qrCode && (
              <img
                src={qrCode}
                alt="MFA QR Code"
                style={{
                  width: '200px',
                  height: '200px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-primary)',
                }}
              />
            )}
          </div>

          <div style={{
            padding: '12px',
            borderRadius: '8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '0 0 4px' }}>
              Can't scan? Enter this key manually:
            </p>
            <code style={{
              fontSize: 'var(--text-sm)',
              fontFamily: 'monospace',
              letterSpacing: '2px',
              color: 'var(--text-primary)',
              wordBreak: 'break-all',
            }}>
              {manualKey}
            </code>
          </div>

          <button
            className="btn btn-primary"
            onClick={() => setStep('verify')}
            style={{ width: '100%' }}
          >
            Next: Verify Code
          </button>
        </div>
      )}

      {/* Verify Step */}
      {step === 'verify' && (
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>Step 2: Verify Code</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginBottom: '16px' }}>
            Enter the 6-digit code shown in your authenticator app.
          </p>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={verifyCode}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerifyCode(val);
                setError('');
              }}
              onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }}
              className="input input-lg"
              placeholder="000000"
              style={{
                flex: 1,
                textAlign: 'center',
                fontFamily: 'monospace',
                fontSize: '24px',
                letterSpacing: '8px',
              }}
              autoFocus
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setStep('generate')}
              style={{ flex: 1 }}
            >
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={handleVerify}
              disabled={isLoading || verifyCode.length !== 6}
              style={{ flex: 1 }}
            >
              {isLoading ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </div>
      )}

      {/* Recovery Codes Step */}
      {step === 'recovery' && (
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>
            {mfaEnabled ? 'New Recovery Codes' : 'Step 3: Save Recovery Codes'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginBottom: '16px' }}>
            Save these recovery codes in a secure location. Each code can only be used once.
            <strong> They will not be shown again.</strong>
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            padding: '16px',
            borderRadius: '8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            marginBottom: '16px',
          }}>
            {recoveryCodes.map((code, i) => (
              <code key={i} style={{
                fontFamily: 'monospace',
                fontSize: 'var(--text-sm)',
                padding: '4px 8px',
                color: 'var(--text-primary)',
              }}>
                {i + 1}. {code}
              </code>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-secondary" onClick={copyRecoveryCodes} style={{ flex: 1 }}>
              Copy to Clipboard
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setMfaEnabled(true);
                setRecoveryCodesRemaining(recoveryCodes.length);
                setRecoveryCodes([]);
                setStep('status');
              }}
              style={{ flex: 1 }}
            >
              I've Saved My Codes
            </button>
          </div>
        </div>
      )}

      {/* Disable MFA */}
      {step === 'complete' && mfaEnabled && (
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>Disable MFA</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginBottom: '16px' }}>
            Enter your current 6-digit code to disable MFA. This will remove the second
            factor requirement from your account.
          </p>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={disableCode}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setDisableCode(val);
                setError('');
              }}
              onKeyDown={e => { if (e.key === 'Enter') handleDisable(); }}
              className="input input-lg"
              placeholder="000000"
              style={{
                flex: 1,
                textAlign: 'center',
                fontFamily: 'monospace',
                fontSize: '24px',
                letterSpacing: '8px',
              }}
              autoFocus
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-secondary" onClick={() => setStep('status')} style={{ flex: 1 }}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDisable}
              disabled={isLoading || disableCode.length !== 6}
              style={{ flex: 1 }}
            >
              {isLoading ? 'Disabling...' : 'Disable MFA'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFASetupPage;
